/**
 * TAN-8655 — Título aportado por la propia página.
 *
 * Lo renderizan las pantallas que se identifican con un dato y no con un rótulo fijo: la lección se
 * llama por su nombre, el progreso de un alumno por el del alumno. La ruta no puede saberlo, así
 * que el título lo pone la página. Su ruta debe declarar `isTitleOwnedByPage: true` en
 * `routesConfig`, pero ese campo **no se lee en runtime**: es la declaración contra la que
 * `routeTitles.registry.test.ts` verifica que config y pantallas no se separen.
 *
 * En ejecución los dos escritores se encadenan: `RouteChangeHandler` escribe siempre el título de
 * la ruta y este componente lo refina anteponiendo el dato. El orden lo garantiza el árbol —el
 * handler es hermano y se renderiza antes del `<Switch>`—, no una supresión.
 *
 * ```tsx
 * <PageTitle value={lessonName} />
 * ```
 *
 * **Por qué es un componente y no un hook.** Un hook obliga a llamarlo incondicionalmente y en el
 * mismo orden en cada render; colarlo dentro de un `if` o después de un `return` temprano —fácil en
 * páginas con estados de carga— rompe el orden de hooks, y en esta app eso **no** degrada a "se ve
 * mal el título": `ChunkLoadErrorBoundary` relanza cualquier error que no sea de carga de chunk (ver su
 * `componentDidCatch`), así que React desmonta el árbol y la aplicación entera se queda en blanco.
 *
 * Como componente ese fallo no existe: renderizarlo condicionalmente es legítimo, y si una página
 * sale antes de pintarlo el único efecto es que el título se queda en el genérico de la ruta. El
 * peor caso posible es cosmético, que es justo el requisito.
 *
 * No renderiza marcado.
 */

import { useContext, useEffect } from 'react';

import { PageTitleContext } from 'app/router/PageTitleContext';
import { composeTitleSegments } from 'app/router/pageTitleUtils';

type PageTitleProps = {
  /**
   * Lo que la página sabe y la ruta no (nombre de la lección, del alumno…). Mientras no ha llegado
   * se escribe el título de ruta solo, y se recompone al llegar: la pestaña nunca queda en blanco
   * ni muestra únicamente el nombre del tenant.
   */
  value?: string;
};

function PageTitle({ value }: PageTitleProps): null {
  const { appName, routeTitle } = useContext(PageTitleContext);

  useEffect(() => {
    const nextTitle = composeTitleSegments([value, routeTitle, appName]);

    // Nunca se escribe cadena vacía: dejaría la pestaña en blanco en vez del título estático de
    // `index.html`, que es peor que un título genérico.
    if (nextTitle) {
      document.title = nextTitle;
    }
  }, [appName, routeTitle, value]);

  return null;
}

export default PageTitle;
